<template>
  <div class="license-config">
    <b-alert v-if="batchIds.length" variant="warning" show>
      Editing license data for
      {{ (batchIds.length + 1) | pluralize("license") }}
    </b-alert>
    <b-alert v-if="batchIds.length && resetConfig" variant="danger" show>
      License config not consistent across selected licenses,<br />
      starting with empty config
    </b-alert>
    <div
      v-if="includeExpiration"
      class="d-flex justify-content-between align-items-center"
    >
      <h4>License Data</h4>
      <b-link
        v-if="hasPermission('audit.access')"
        :href="`/admin/audit-log?license_id=${license.id}`"
        target="_blank"
        class="btn btn-outline-primary"
      >
        <i class="icon-list"></i>
        Audit Log
      </b-link>
    </div>
    <b-tabs @input="cloneData" class="mb-4">
      <b-tab title="Form">
        Config:
        <b-form @submit.prevent="saveConfig">
          <b-row>
            <b-col>
              <b-input
                v-model="configInputs.key"
                list="license-config-opts"
                :disabled="isLoading"
                placeholder="key"
                aria-label="key"
              />
              <datalist id="license-config-opts">
                <option
                  v-for="option in configOptions"
                  :key="option"
                  :value="option"
                />
              </datalist>
            </b-col>

            <b-col>
              <b-input v-if="!configInputs.key" disabled />

              <b-form-select
                v-else-if="configInputType === 'boolean'"
                v-model="configInputs.value"
                :disabled="isLoading"
              >
                <option :value="true">True</option>
                <option :value="false">False</option>
              </b-form-select>

              <b-input
                v-else-if="configInputType === 'number'"
                v-model.number="configInputs.value"
                :disabled="isLoading"
                type="number"
                placeholder="value"
                aria-label="value"
              />

              <b-form-textarea
                v-else
                v-model="configInputs.value"
                :disabled="isLoading"
                placeholder="value"
                aria-label="value"
                max-rows="6"
              />
            </b-col>
            <b-col sm="auto">
              <b-btn
                type="submit"
                variant="link"
                class="pl-0"
                aria-label="Add to config"
              >
                <i class="icon-plus"></i>
              </b-btn>
            </b-col>
          </b-row>
        </b-form>
        <table class="table table-striped mb-5">
          <tbody>
            <tr v-for="[key, value] in Object.entries(config)" :key="key">
              <td>{{ key }}</td>
              <td>
                <div class="license-config__data">{{ value }}</div>
              </td>
              <td class="text-right">
                <b-btn
                  @click="removeConfig(key)"
                  variant="link"
                  class="p-0"
                  aria-label="Remove item"
                >
                  <i class="icon-close"></i>
                </b-btn>
              </td>
            </tr>
          </tbody>
        </table>
        Extras:
        <b-form @submit.prevent="saveExtras">
          <b-row>
            <b-col>
              <b-input
                v-model="extrasInputs.key"
                list="license-extras-opts"
                placeholder="key"
                aria-label="key"
              />
              <datalist id="license-extras-opts">
                <option
                  v-for="option in extrasOptions"
                  :key="option"
                  :value="option"
                />
              </datalist>
            </b-col>

            <b-col>
              <b-input v-if="!extrasInputs.key" disabled />

              <b-form-select
                v-else-if="extrasInputType === 'boolean'"
                v-model="extrasInputs.value"
              >
                <option :value="true">True</option>
                <option :value="false">False</option>
              </b-form-select>

              <b-input
                v-else-if="extrasInputType === 'number'"
                v-model.number="extrasInputs.value"
                type="number"
                placeholder="value"
                aria-label="value"
              />

              <b-form-textarea
                v-else
                v-model="extrasInputs.value"
                :disabled="isLoading"
                placeholder="value"
                aria-label="value"
                max-rows="6"
              />
            </b-col>
            <b-col sm="auto">
              <b-btn
                type="submit"
                variant="link"
                class="pl-0"
                aria-label="Add to extras"
              >
                <i class="icon-plus"></i>
              </b-btn>
            </b-col>
          </b-row>
        </b-form>
        <table class="table table-striped">
          <tbody>
            <tr v-for="[key, value] in Object.entries(extras)" :key="key">
              <td>{{ key }}</td>
              <td>
                <div class="license-config__data">{{ value }}</div>
              </td>
              <td class="text-right">
                <b-btn
                  @click="removeExtra(key)"
                  variant="link"
                  class="p-0"
                  aria-label="Remove item"
                >
                  <i class="icon-close"></i>
                </b-btn>
              </td>
            </tr>
          </tbody>
        </table>
      </b-tab>

      <b-tab title="JSON" lazy>
        <form @submit.prevent="updateJson">
          <NanInput v-model="json" type="textarea" @input="$v.json.$touch" />

          <div class="text-danger" v-if="!$v.json.validJson">Invalid JSON</div>

          <b-btn type="submit" :disabled="isLoading || $v.json.$invalid">
            Save Changes
          </b-btn>
        </form>
      </b-tab>
    </b-tabs>

    <template v-if="includeExpiration">
      <h4>License Expiration</h4>
      <b-row>
        <b-col>
          <NanInput
            v-model="expired_time"
            placeholder="mm/dd/yyyy"
            type="date"
            @input="expiryChanged = true"
          />
        </b-col>
        <b-col>
          <b-btn
            variant="primary"
            :disabled="!expiryChanged || expired_time === null"
            @click="saveExpiration"
          >
            Save Expiration
          </b-btn>
        </b-col>
      </b-row>
      <small
        v-if="expired_time !== null && new Date(expired_time) < new Date()"
        class="text-danger"
      >
        this license will be marked as expired
      </small>
    </template>
  </div>
</template>

<script>
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import API from "@/api";
import { mapGetters } from "vuex";

export default {
  mixins: [validationMixin],

  props: {
    batchIds: {
      type: Array,
      default: () => []
    },
    license: {
      type: Object,
      required: true
    },
    includeExpiration: {
      type: Boolean,
      default: false
    },
    noRequest: {
      type: Boolean,
      default: false
    },
    resetConfig: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    isLoading: false,
    config: {},
    configInputs: {
      key: "",
      value: ""
    },
    configOptions: [
      "default-locomotion-on",
      "default-room-id",
      "default-room-name",
      "network-type",
      "network-licensed-mode",
      "network-licensed-ip",
      "network-licensed-port",
      "network-licensed-voice-port",
      "network-licensed-protocol",
      "plugin-server-addr",
      "plugin-server-port",
      "v2-photon-url",
      "v2-workspace-url"
    ],
    configTypes: {
      "default-locomotion-on": "boolean",
      "network-licensed-port": "number",
      "network-licensed-voice-port": "number",
      "plugin-server-port": "number"
    },
    extras: {},
    extrasInputs: {
      key: "",
      value: ""
    },
    extrasOptions: [],
    expired_time: null,
    expiryChanged: false,
    json: ""
  }),

  computed: {
    ...mapGetters(["hasPermission"]),

    configInputType() {
      return this.configTypes[this.configInputs.key] || "text";
    },

    extrasInputType() {
      return this.configTypes[this.extrasInputs.key] || "text";
    }
  },

  async created() {
    const { results } = await API.getLicenseCapabilityTypes();
    Object.assign(this.configTypes, results);
    this.extrasOptions = Object.keys(results);
    this.cloneData();
  },

  methods: {
    cloneData() {
      // reset config if requested
      const copyFrom = this.resetConfig ? {} : this.license.data;
      const { config = {}, extras = {} } = copyFrom;
      this.config = config;
      this.extras = extras;
      this.expired_time = this.batchIds.length
        ? null
        : new Date(this.license.expired_time).toISOString().slice(0, 10);
      this.expiryChanged = false;
      this.json = JSON.stringify({ config, extras }, null, 2);
    },

    async updateData(data = {}) {
      if (this.isLoading) return;

      if (!this.noRequest) {
        const { success } = await API.licenseData({
          license: this.license.id,
          data: data
        });
        if (!success) {
          throw new Error("The server could not update the licenses");
        }

        await Promise.all(
          this.batchIds.map(id => {
            return API.licenseData({
              license: id,
              data: data
            });
          })
        );

        const n = this.batchIds.length + 1;
        this.$notify({
          type: "success",
          text: `License config updated for ${n} license${n > 1 ? "s" : ""}`
        });
      }

      this.config = data.config || {};
      this.extras = data.extras || {};
      this.$emit("update", { data });
    },

    async saveConfig() {
      if (this.$v.configInputs.$invalid) return;

      const { key, value } = this.configInputs;

      await this.updateData({
        extras: this.extras,
        config: {
          ...this.config,
          [key.trim()]: (value.trim && value.trim()) || value
        }
      });

      this.configInputs.key = this.configInputs.value = "";
    },

    async removeConfig(key) {
      const newConfig = {
        ...this.config
      };
      delete newConfig[key];
      await this.updateData({
        extras: this.extras,
        config: newConfig
      });
    },

    async saveExpiration() {
      const { expired_time } = this;
      const { success } = await API.setLicenseExpiry({
        license_id: this.license.id,
        expired_time
      });
      if (!success) {
        throw new Error("Failed to update license expiration");
      }

      await Promise.all(
        this.batchIds.map(id => {
          return API.setLicenseExpiry({
            license_id: id,
            expired_time
          });
        })
      );

      const n = this.batchIds.length + 1;
      this.$notify({
        type: "success",
        text: `License expiration updated for ${n} license${n > 1 ? "s" : ""}`
      });

      this.expiryChanged = false;
      this.$emit("update", { expired_time });
    },

    async saveExtras() {
      if (this.$v.extrasInputs.$invalid) return;

      const { key, value } = this.extrasInputs;

      await this.updateData({
        config: this.config,
        extras: {
          ...this.extras,
          [key.trim()]: (value.trim && value.trim()) || value
        }
      });

      this.extrasInputs.key = this.extrasInputs.value = "";
    },

    async removeExtra(key) {
      const newExtras = {
        ...this.extras
      };
      delete newExtras[key];
      await this.updateData({
        config: this.config,
        extras: newExtras
      });
    },

    async updateJson() {
      const data = this.json ? JSON.parse(this.json) : {};
      await this.updateData(data);
    }
  },

  validations: {
    configInputs: {
      key: { required },
      value: { required }
    },
    extrasInputs: {
      key: { required },
      value: { required }
    },
    json: {
      validJson() {
        if (!this.json) return true;
        try {
          JSON.parse(this.json);
          return true;
        } catch (e) {
          return false;
        }
      },
      changed() {
        if (this.batchIds.length) return true;
        return this.json.trim() !== JSON.stringify(this.license.data, null, 2);
      }
    }
  }
};
</script>

<style lang="scss">
.license-config {
  &__data {
    max-height: 80px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-all;
  }
}
</style>
